import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus,
  Trash2,
  Copy,
  Check,
  Webhook,
  FileSearch,
  Link as LinkIcon,
  AlertCircle
} from 'lucide-react';

interface EventTrigger {
  id: string;
  job_id: string;
  job_name: string;
  type: 'webhook' | 'file_watch' | 'job_chain';
  config: any;
  secret_token?: string;
  created_at: string;
}

interface Job {
  id: string;
  name: string;
}

export default function EventTriggers() {
  const [triggers, setTriggers] = useState<EventTrigger[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const [form, setForm] = useState({
    job_id: '',
    type: 'webhook' as 'webhook' | 'file_watch' | 'job_chain',
    config: {
      // Webhook
      method: 'POST',
      // File watch
      path: '',
      event: 'change',
      // Job chain
      parent_job_id: '',
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [triggersRes, jobsRes] = await Promise.all([
        axios.get('/api/triggers'),
        axios.get('/api/jobs'),
      ]);
      setTriggers(triggersRes.data);
      setJobs(jobsRes.data);
      setError('');
    } catch (err) {
      setError('Failed to load event triggers');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await axios.post('/api/triggers', form);
      await fetchData();
      setShowCreateForm(false);
      resetForm();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create trigger');
    }
  };

  const deleteTrigger = async (id: string) => {
    if (!confirm('Are you sure you want to delete this trigger?')) return;

    try {
      await axios.delete(`/api/triggers/${id}`);
      await fetchData();
    } catch (err) {
      alert('Failed to delete trigger');
      console.error(err);
    }
  };

  const copyWebhookUrl = (token: string) => {
    const url = `${window.location.origin}/api/webhooks/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const resetForm = () => {
    setForm({
      job_id: '',
      type: 'webhook',
      config: {
        method: 'POST',
        path: '',
        event: 'change',
        parent_job_id: '',
      },
    });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-white text-center">Loading event triggers...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Event Triggers</h1>
          <p className="text-slate-400">Configure webhooks, file watchers, and job chains</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Create Trigger</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
          {error}
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Create Event Trigger</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Job *
              </label>
              <select
                value={form.job_id}
                onChange={(e) => setForm({ ...form, job_id: e.target.value })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a job...</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Trigger Type *
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as any })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="webhook">Webhook</option>
                <option value="file_watch">File Watch</option>
                <option value="job_chain">Job Chain</option>
              </select>
            </div>

            {/* Webhook Config */}
            {form.type === 'webhook' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  HTTP Method
                </label>
                <select
                  value={form.config.method}
                  onChange={(e) => setForm({ ...form, config: { ...form.config, method: e.target.value } })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                  <option value="PUT">PUT</option>
                </select>
                <p className="mt-2 text-xs text-slate-400">
                  A unique webhook URL will be generated after creation
                </p>
              </div>
            )}

            {/* File Watch Config */}
            {form.type === 'file_watch' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    File Path *
                  </label>
                  <input
                    type="text"
                    value={form.config.path}
                    onChange={(e) => setForm({ ...form, config: { ...form.config, path: e.target.value } })}
                    placeholder="/opt/weretrade/data/*.json"
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="mt-2 text-xs text-slate-400">
                    Supports wildcards (*, **, etc.)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Event
                  </label>
                  <select
                    value={form.config.event}
                    onChange={(e) => setForm({ ...form, config: { ...form.config, event: e.target.value } })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="add">File Created</option>
                    <option value="change">File Modified</option>
                    <option value="unlink">File Deleted</option>
                  </select>
                </div>
              </div>
            )}

            {/* Job Chain Config */}
            {form.type === 'job_chain' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Parent Job *
                </label>
                <select
                  value={form.config.parent_job_id}
                  onChange={(e) => setForm({ ...form, config: { ...form.config, parent_job_id: e.target.value } })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select parent job...</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-slate-400">
                  This job will run automatically after the parent job completes successfully
                </p>
              </div>
            )}

            <div className="flex items-center justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Create Trigger
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Triggers List */}
      <div className="space-y-4">
        {triggers.length === 0 ? (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
            <Webhook className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No event triggers configured</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Create Your First Trigger</span>
            </button>
          </div>
        ) : (
          triggers.map((trigger) => (
            <div key={trigger.id} className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  {trigger.type === 'webhook' && <Webhook className="w-6 h-6 text-blue-400 mt-1" />}
                  {trigger.type === 'file_watch' && <FileSearch className="w-6 h-6 text-green-400 mt-1" />}
                  {trigger.type === 'job_chain' && <LinkIcon className="w-6 h-6 text-purple-400 mt-1" />}

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{trigger.job_name}</h3>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      trigger.type === 'webhook'
                        ? 'bg-blue-900/50 text-blue-300'
                        : trigger.type === 'file_watch'
                        ? 'bg-green-900/50 text-green-300'
                        : 'bg-purple-900/50 text-purple-300'
                    }`}>
                      {trigger.type.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => deleteTrigger(trigger.id)}
                  className="p-2 text-red-400 hover:bg-red-900/50 rounded transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Trigger-specific details */}
              {trigger.type === 'webhook' && trigger.secret_token && (
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-2">Webhook URL:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm text-blue-300 bg-slate-800 px-3 py-2 rounded font-mono break-all">
                      {window.location.origin}/api/webhooks/{trigger.secret_token}
                    </code>
                    <button
                      onClick={() => copyWebhookUrl(trigger.secret_token!)}
                      className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                      title="Copy URL"
                    >
                      {copiedToken === trigger.secret_token ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Method: <strong>{trigger.config.method || 'POST'}</strong>
                  </p>
                </div>
              )}

              {trigger.type === 'file_watch' && (
                <div className="bg-slate-900 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400 mb-1">Path:</p>
                      <code className="text-green-300 bg-slate-800 px-2 py-1 rounded font-mono">
                        {trigger.config.path}
                      </code>
                    </div>
                    <div>
                      <p className="text-slate-400 mb-1">Event:</p>
                      <span className="text-white">{trigger.config.event}</span>
                    </div>
                  </div>
                </div>
              )}

              {trigger.type === 'job_chain' && (
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Triggers after:</p>
                  <p className="text-white">
                    {jobs.find((j) => j.id === trigger.config.parent_job_id)?.name || 'Unknown Job'}
                  </p>
                </div>
              )}

              <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                <span>Created: {new Date(trigger.created_at).toLocaleString()}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
